import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2, MessageCircle, Reply, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "./Avatar";
import { Chip, inputCls } from "./Chips";
import { errMsg, http } from "../lib/api";

const ago = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "ką tik";
  if (m < 60) return `prieš ${m} min.`;
  if (m < 1440) return `prieš ${Math.floor(m / 60)} val.`;
  return new Date(iso).toLocaleDateString("lt-LT");
};

const CommentForm = ({ onSend, placeholder, testId = "comment", autoFocus, onCancel }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    if (await onSend(text.trim())) setText("");
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="flex gap-3 items-end">
      <textarea data-testid={`${testId}-input`} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={2}
        autoFocus={autoFocus} placeholder={placeholder} className={`${inputCls} resize-none flex-1`}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); if (e.key === "Escape") onCancel?.(); }} />
      <button data-testid={`${testId}-submit`} disabled={busy || !text.trim()} title="Siųsti"
        className="p-3.5 rounded-full bg-[var(--c-orange)] text-white hover:bg-[#ff7a26] transition-colors disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </button>
    </form>
  );
};

const LikeBtn = ({ c, onLike }) => (
  <button data-testid={`comment-like-${c.id}`} onClick={() => onLike(c)}
    className={`flex items-center gap-1 text-xs transition-colors ${c.liked ? "text-[var(--c-orange)]" : "text-zinc-500 hover:text-white"}`}>
    <Heart className="w-3.5 h-3.5" fill={c.liked ? "currentColor" : "none"} />
    <span data-testid={`comment-likes-${c.id}`} className="font-mono">{c.likes || 0}</span>
  </button>
);

const CommentItem = ({ c, onDelete, onReply, onLike, small }) => (
  <div data-testid={`comment-${c.id}`} className="flex gap-3 group rise">
    <Link to={`/autorius/${c.author.user_id}`}><Avatar user={c.author} className={small ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm"} /></Link>
    <div className="min-w-0 flex-1">
      <p className="text-sm flex flex-wrap items-center gap-x-2">
        <Link to={`/autorius/${c.author.user_id}`} data-testid={`comment-author-${c.id}`} className="font-semibold hover:text-[var(--c-orange)] transition-colors">{c.author.name}</Link>
        {c.is_author && <span data-testid={`comment-author-badge-${c.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[var(--c-violet)]/20 text-[var(--c-violet)]">Autorius</span>}
        <span className="text-xs text-zinc-500">{ago(c.created_at)}</span>
      </p>
      <p data-testid={`comment-text-${c.id}`} className="text-sm text-zinc-300 mt-1 whitespace-pre-line break-words">{c.text}</p>
      <div className="flex items-center gap-4 mt-1.5">
        <LikeBtn c={c} onLike={onLike} />
        <button data-testid={`comment-reply-${c.id}`} onClick={onReply}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-[var(--c-orange)] transition-colors"><Reply className="w-3.5 h-3.5" />Atsakyti</button>
      </div>
    </div>
    {c.can_delete && (
      <button data-testid={`comment-delete-${c.id}`} onClick={() => onDelete(c)} title="Ištrinti"
        className="self-start p-1.5 rounded-lg text-zinc-600 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-400 transition-[opacity,color]">
        <Trash2 className="w-4 h-4" />
      </button>
    )}
  </div>
);

const Thread = ({ c, replyTo, setReplyTo, onSend, onDelete, onLike }) => (
  <div className="space-y-4">
    <CommentItem c={c} onDelete={onDelete} onLike={onLike} onReply={() => setReplyTo(c.id)} />
    {(c.replies.length > 0 || replyTo === c.id) && (
      <div data-testid={`comment-replies-${c.id}`} className="ml-12 pl-4 border-l border-[var(--c-line)] space-y-4">
        {c.replies.map((r) => <CommentItem key={r.id} c={r} small onDelete={onDelete} onLike={onLike} onReply={() => setReplyTo(c.id)} />)}
        {replyTo === c.id && (
          <CommentForm testId="reply" autoFocus placeholder={`Atsakyti ${c.author.name}…`} onCancel={() => setReplyTo(null)}
            onSend={(t) => onSend(t, c.id)} />
        )}
      </div>
    )}
  </div>
);

export const Comments = ({ song }) => {
  const [items, setItems] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [sort, setSort] = useState("top");
  const load = useCallback(() => http.get(`/songs/${song.id}/comments`, { params: { sort } }).then((r) => setItems(r.data)), [song.id, sort]);
  useEffect(() => { load(); }, [load]);
  const total = items ? items.reduce((n, c) => n + 1 + c.replies.length, 0) : "";

  const send = async (text, parentId = null) => {
    try {
      const { data } = await http.post(`/songs/${song.id}/comments`, { text, parent_id: parentId });
      setItems((xs) => (parentId ? xs.map((c) => (c.id === parentId ? { ...c, replies: [...c.replies, data] } : c)) : [data, ...xs]));
      if (parentId) setReplyTo(null);
      return true;
    } catch (err) { toast.error(errMsg(err)); return false; }
  };
  const like = async (c) => {
    try {
      const { data } = await http.post(`/comments/${c.id}/like`);
      const upd = (x) => (x.id === c.id ? { ...x, ...data } : x);
      setItems((xs) => xs.map((x) => ({ ...upd(x), replies: x.replies.map(upd) })));
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (c) => {
    if (!window.confirm(c.parent_id ? "Ištrinti atsakymą?" : "Ištrinti komentarą ir visus jo atsakymus?")) return;
    try {
      await http.delete(`/comments/${c.id}`);
      setItems((xs) => xs.filter((x) => x.id !== c.id).map((x) => ({ ...x, replies: x.replies.filter((r) => r.id !== c.id) })));
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <section data-testid="comments-section" className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
          <MessageCircle className="w-3.5 h-3.5" />Komentarai <span data-testid="comments-count" className="font-mono">{total}</span>
        </p>
        <div className="flex gap-2">
          <Chip testId="comments-sort-top" active={sort === "top"} onClick={() => setSort("top")}>Populiariausi</Chip>
          <Chip testId="comments-sort-new" active={sort === "new"} onClick={() => setSort("new")}>Naujausi</Chip>
        </div>
      </div>
      <CommentForm onSend={(t) => send(t)} placeholder="Pasidalinkite įspūdžiais su kūrėju…" />
      <div data-testid="comments-list" className="space-y-6 mt-6">
        {items?.length === 0 && <p data-testid="comments-empty" className="text-sm text-zinc-500">Komentarų dar nėra – būkite pirmas!</p>}
        {items?.map((c) => <Thread key={c.id} c={c} replyTo={replyTo} setReplyTo={setReplyTo} onSend={send} onDelete={del} onLike={like} />)}
      </div>
    </section>
  );
};
